import { Component, forwardRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  DATE_RANGE_CONTROL,
  DATE_RANGE_LABEL,
  DATE_RANGE_MESSAGE,
  DATE_RANGE_STATUS,
} from './date-range.constants';

/** Value emitted through registerOnChange and accepted by writeValue. */
export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
  totalDays?: number | null;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Compact Start/End date range picker (ControlValueAccessor) around two `one-datepicker`,
 * styled like the purchase-orders list filters (32px boxes, inline label left of the value).
 *
 * The internal FormGroup holds startDate, endDate and totalDays; every change emits the full
 * `form.getRawValue()` to the parent. endDate is always normalized to 23:59:59.000 UTC and
 * totalDays counts calendar days inclusively (end − start + 1).
 */
@Component({
  selector: 'app-date-range',
  templateUrl: './date-range.component.html',
  styleUrls: ['./date-range.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangeComponent),
      multi: true,
    },
  ],
})
export class DateRangeComponent implements ControlValueAccessor, OnChanges, OnInit, OnDestroy {
  /* istanbul ignore next -- decorator metadata */
  @Input() disabled: boolean;
  /* istanbul ignore next -- decorator metadata */
  @Input() isCampaignManager: boolean;
  /* istanbul ignore next -- decorator metadata */
  @Input() hasEndDate: boolean;
  /* istanbul ignore next -- decorator metadata */
  @Input() days = 30;
  /* istanbul ignore next -- decorator metadata */
  @Input() minDaysEndDate = 1;
  /* istanbul ignore next -- decorator metadata */
  @Input() isCollision = false;
  /* istanbul ignore next -- decorator metadata */
  @Input() minDaysDefault = 1;
  /* istanbul ignore next -- decorator metadata */
  @Input() messageErrorStartDate: string;
  /* istanbul ignore next -- decorator metadata */
  @Input() messageErrorEndDate: string;
  /* istanbul ignore next -- decorator metadata */
  @Input() status = '';

  // Constants re-exposed for the template (project constants convention).
  readonly CTRL = DATE_RANGE_CONTROL;
  readonly LABEL = DATE_RANGE_LABEL;
  readonly MSG = DATE_RANGE_MESSAGE;

  readonly form = new FormGroup({
    [DATE_RANGE_CONTROL.START_DATE]: new FormControl(null),
    [DATE_RANGE_CONTROL.END_DATE]: new FormControl(null),
    [DATE_RANGE_CONTROL.TOTAL_DAYS]: new FormControl(null),
  });

  // Calendar bounds. minimumStartDate = today + minDaysDefault; the end bounds are recomputed
  // on every startDate change and on writeValue (see _computeMinimumEndDate).
  minimumStartDate: Date | null = null;
  minimumEndDate: Date | null = null;
  maximumDate: Date | null = null;

  private _cvaDisabled = false;
  private readonly destroy$ = new Subject<void>();
  // Last values seen by the valueChanges handlers. MatDatepickerInput re-emits valueChanges
  // (via updateValueAndValidity) whenever its [min]/[max] bindings change — even with the
  // value unchanged — and those emissions land mid-change-detection (ngOnChanges recomputes
  // the bounds), where touching the parent's pristine/touched state would throw
  // ExpressionChangedAfterItHasBeenChecked. Value-identical emissions are skipped instead.
  private _lastStartDate: unknown = null;
  private _lastEndDate: unknown = null;

  ngOnChanges(changes: SimpleChanges): void {
    // Spec: recalcular minimumStartDate en cada ngOnChanges.
    this.minimumStartDate = this._addDays(new Date(), this.minDaysDefault);

    if (!changes['hasEndDate']?.firstChange && !this.hasEndDate) {
      this.form.get(this.CTRL.END_DATE).setValue(null);
    }

    // Keep the derived end bounds consistent when an input changes after a start date was
    // already picked (defensive — the spec only requires the start-change path).
    const start = this.form.get(this.CTRL.START_DATE).value;
    if (this._isValidDate(start)) {
      this.minimumEndDate = this._computeMinimumEndDate(start);
      this.maximumDate = this._addDays(start, this.days);
    }

    this._updateDisabled();
  }

  ngOnInit(): void {
    this.form.get(this.CTRL.START_DATE).valueChanges.pipe(takeUntil(this.destroy$)).subscribe(start => {
      if (start === this._lastStartDate) {
        return;
      }
      this._lastStartDate = start;
      if (this._isValidDate(start)) {
        this.minimumEndDate = this._computeMinimumEndDate(start);
        this.maximumDate = this._addDays(start, this.days);
        // Spec: reinicia endDate a null (solo cuando el nuevo start es una fecha válida).
        if (this.form.get(this.CTRL.END_DATE).value != null) {
          this.form.get(this.CTRL.END_DATE).setValue(null);
        }
      }
      this._recalcTotalDays();
      this._updateDisabled();
      this._onTouched();
      this._emitValue();
    });

    this.form.get(this.CTRL.END_DATE).valueChanges.pipe(takeUntil(this.destroy$)).subscribe(end => {
      if (end === this._lastEndDate) {
        return;
      }
      if (this._isValidDate(end)) {
        // Spec: 23:59:59.000 UTC sin emitir un evento adicional del form — the control is
        // updated silently and the single _emitValue below carries the normalized endDate.
        const normalized = this._endOfUtcDay(end);
        this._lastEndDate = normalized;
        this.form.get(this.CTRL.END_DATE).setValue(normalized, { emitEvent: false });
      } else {
        this._lastEndDate = end;
      }
      this._recalcTotalDays();
      this._onTouched();
      this._emitValue();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── ControlValueAccessor ────────────────────────────────────────────────────────────────────

  writeValue(value: DateRange | null): void {
    // Spec: solo actúa cuando llega un value.
    if (!value) {
      return;
    }
    const startDate = this._isValidDate(value.startDate) ? value.startDate : null;
    const endDate = this._isValidDate(value.endDate) ? this._endOfUtcDay(value.endDate) : null;
    const totalDays = startDate && endDate ? this._diffInclusiveDays(startDate, endDate) : null;
    // Sync the handler guards: the silent setValue below never fires them, and a stale guard
    // would let the next MatDatepickerInput no-op emission run the start handler and wipe the
    // loaded endDate.
    this._lastStartDate = startDate;
    this._lastEndDate = endDate;
    if (startDate) {
      this.minimumEndDate = this._computeMinimumEndDate(startDate);
      this.maximumDate = this._addDays(startDate, this.days);
    }
    this.form.setValue(
      {
        [this.CTRL.START_DATE]: startDate,
        [this.CTRL.END_DATE]: endDate,
        [this.CTRL.TOTAL_DAYS]: totalDays,
      },
      { emitEvent: false },
    );
    this._updateDisabled();
  }

  registerOnChange(fn: (value: DateRange) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._cvaDisabled = isDisabled;
    this._updateDisabled();
  }

  // ─── Template API ─────────────────────────────────────────────────────────────────────────────

  get hasStartDateValue(): boolean {
    return this._isValidDate(this.form.get(this.CTRL.START_DATE).value);
  }

  get hasEndDateValue(): boolean {
    return this._isValidDate(this.form.get(this.CTRL.END_DATE).value);
  }

  // "Campaign duration must not exceed N days", N = days + 1 (or 31 when days is unset).
  get campaignNote(): string {
    const maxDays = this.days != null ? this.days + 1 : 31;
    return DATE_RANGE_MESSAGE.campaignDuration(maxDays);
  }

  // Errors of a control, surfaced only once it is dirty or touched — for the parent's error
  // mechanism (appFormErrors-style).
  errorsFor(key: string): ValidationErrors | null {
    const ctrl = this.form.get(key);
    if (!ctrl || !(ctrl.dirty || ctrl.touched)) {
      return null;
    }
    return ctrl.errors;
  }

  clearStartDate(): void {
    this.form.get(this.CTRL.START_DATE).setValue(null);
  }

  clearEndDate(): void {
    this.form.get(this.CTRL.END_DATE).setValue(null);
  }

  // ─── Internals ───────────────────────────────────────────────────────────────────────────────

  // Spec: emitir form.getRawValue() en cada cambio del form interno. Emitted explicitly from
  // the valueChanges handlers instead of through a form.valueChanges subscription: the group
  // observable also fires for MatDatepickerInput's value-identical re-emissions (min/max
  // binding updates), which would push unchanged values into the parent mid-change-detection.
  private _emitValue(): void {
    this._onChange(this.form.getRawValue() as DateRange);
  }

  // totalDays = end − start + 1 (inclusive); null while either bound is missing so an end
  // without a start never breaks the calculation. Only writes when the value actually
  // changed, so no-op handler runs stay silent.
  private _recalcTotalDays(): void {
    const start = this.form.get(this.CTRL.START_DATE).value;
    const end = this.form.get(this.CTRL.END_DATE).value;
    const totalDays = this._isValidDate(start) && this._isValidDate(end)
      ? this._diffInclusiveDays(start, end)
      : null;
    const totalCtrl = this.form.get(this.CTRL.TOTAL_DAYS);
    if (totalCtrl.value !== totalDays) {
      totalCtrl.setValue(totalDays);
    }
  }

  // Gates: `disabled` input and the parent's setDisabledState disable both pickers; a non-empty
  // status other than SCHEDULED disables the start picker; the end picker stays disabled until a
  // start date exists. enable/disable run with emitEvent:false (spec: sin emitir eventos del form).
  private _updateDisabled(): void {
    const startCtrl = this.form.get(this.CTRL.START_DATE);
    const endCtrl = this.form.get(this.CTRL.END_DATE);
    const statusBlocksStart = !!this.status && this.status !== DATE_RANGE_STATUS.SCHEDULED;
    const startDisabled = this.disabled === true || this._cvaDisabled || statusBlocksStart;
    const endDisabled = this.disabled === true || this._cvaDisabled || !this.hasStartDateValue;

    if (startDisabled && !startCtrl.disabled) {
      startCtrl.disable({ emitEvent: false });
    } else if (!startDisabled && startCtrl.disabled) {
      startCtrl.enable({ emitEvent: false });
    }
    if (endDisabled && !endCtrl.disabled) {
      endCtrl.disable({ emitEvent: false });
    } else if (!endDisabled && endCtrl.disabled) {
      endCtrl.enable({ emitEvent: false });
    }
  }

  // Greater of (startDate + minDaysEndDate) and (today + minDaysEndDate), both at 23:59:59.000 UTC.
  private _computeMinimumEndDate(start: Date): Date {
    const fromStart = this._endOfUtcDay(this._addDays(start, this.minDaysEndDate));
    const fromToday = this._endOfUtcDay(this._addDays(new Date(), this.minDaysEndDate));
    return fromStart.getTime() >= fromToday.getTime() ? fromStart : fromToday;
  }

  private _endOfUtcDay(date: Date): Date {
    const copy = new Date(date.getTime());
    copy.setUTCHours(23, 59, 59, 0);
    return copy;
  }

  private _addDays(date: Date, days: number): Date {
    return new Date(date.getTime() + days * MS_PER_DAY);
  }

  // Calendar-day difference in UTC, +1 so both boundary days count.
  private _diffInclusiveDays(start: Date, end: Date): number {
    const startDay = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
    const endDay = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());
    return Math.round((endDay - startDay) / MS_PER_DAY) + 1;
  }

  private _isValidDate(value: unknown): value is Date {
    return value instanceof Date && !isNaN(value.getTime());
  }

  private _onChange: (value: DateRange) => void = () => {};
  private _onTouched: () => void = () => {};
}
